/**
 * StatusPill — renders one of the six fixed member-facing bug statuses as a
 * coloured pill.
 *
 * Story: epic-uat-portal-001-11.
 *
 * SECURITY: the status is tester-reachable backend data, so it is NEVER
 * interpolated into a className or into markup — we look the status up in a
 * FIXED label→class table. An unrecognised string falls back to a neutral pill
 * labelled "Unknown"; the raw value is not echoed into the class or the text, so
 * a hostile status can neither inject a class nor render arbitrary content.
 */

import type { MemberStatus } from '@/services/bugBoardService';

const STATUS_CLASSES: Record<MemberStatus, string> = {
  New: 'border-blue-300 bg-blue-50 text-blue-800',
  Triaging: 'border-amber-300 bg-amber-50 text-amber-800',
  'In progress': 'border-indigo-300 bg-indigo-50 text-indigo-800',
  Fixed: 'border-green-300 bg-green-50 text-green-800',
  Closed: 'border-gray-300 bg-gray-100 text-gray-700',
  Duplicate: 'border-purple-300 bg-purple-50 text-purple-800',
};

const FALLBACK_CLASS = 'border-gray-300 bg-gray-100 text-gray-600';

function isKnownStatus(status: string): status is MemberStatus {
  return Object.prototype.hasOwnProperty.call(STATUS_CLASSES, status);
}

export function StatusPill({ status }: { status: string }) {
  const known = isKnownStatus(status);
  // Never put the raw status into the class OR the label when unknown.
  const className = known ? STATUS_CLASSES[status] : FALLBACK_CLASS;
  const label = known ? status : 'Unknown';

  return (
    <span
      data-testid="board-status-pill"
      className={`inline-block rounded-full border px-2.5 py-0.5 text-xs font-medium ${className}`}
    >
      {label}
    </span>
  );
}
